import React from "react"
import { Query } from "react-apollo"
import { Waypoint } from "react-waypoint"
import SearchBar from "../SearchBar"
import gql from "graphql-tag"
import PropTypes from "prop-types"

import "./vennTranscript.scss"

const MIN_SCALE = 1
const MAX_SCALE = 5

class VennTranscript extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      keywordInput: props.initialKeyword || "",
      keyword: props.initialKeyword || "",
      scale: 1,
      snippet: null,
    }

    this.keywordTimeout
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (prevState.keyword !== this.state.keyword && this.state.keyword) {
      this.props.trackEvent("Updated transcript search", {
        productId: this.props.productId,
        productType: this.product && this.product.proprietaryDataset,
        productName: this.product && this.product.name,
        keyword: this.state.keyword,
      })
    }
  }

  incScale = () => {
    this.setState((state) => ({
      scale: Math.min(state.scale + 1, MAX_SCALE),
    }))
  }

  decScale = () => {
    this.setState((state) => ({
      scale: Math.max(state.scale - 1, MIN_SCALE),
    }))
  }

  scrollToSnippet = async (snippet, didRetry = false) => {
    const snippetRef = this[`fulltranscript-${snippet.id}`]
    const scrollRef = this.fulltranscript

    if (!scrollRef) {
      this.setState({ snippet: null })
      return
    }

    if (snippetRef) {
      scrollRef.scrollTo({
        top: snippetRef.offsetTop - scrollRef.offsetTop,
        behavior: "smooth",
      })
    } else if (!didRetry && this.client) {
      try {
        let { data } = await this.client.query({
          query: refetchSnippetsQuery,
          variables: {
            id: this.props.productId,
            keyword: this.state.keyword,
            limit: snippet.index + 10, // fetch a few more snippets so it doesn't look chopped off
          },
        })

        const queryConfig = {
          query: snippetsQuery,
          variables: {
            id: this.props.productId,
            keyword: this.state.keyword,
          },
        }

        const cachedData = this.client.readQuery(queryConfig)

        this.client.writeQuery({
          ...queryConfig,
          data: {
            ...cachedData,
            page: {
              ...cachedData.page,
              snippets: data.page.snippets,
            },
          },
        })

        setTimeout(() => this.scrollToSnippet(snippet), 100)
      } catch (err) {
        this.setState({ snippet: null })
      }
    } else {
      this.setState({ snippet: null })
    }
  }

  selectSnippet = (snippet, i) => {
    this.setState(
      (state, props) => {
        props.trackEvent("Selected snippet result", {
          keyword: state.keyword,
          productId: props.productId,
          productType: this.product && this.product.proprietaryDataset,
          productName: this.product && this.product.name,
          snippetText: snippet.text,
          snippetId: snippet.id,
          snippetIndex: i,
          snippetIndexInTranscript: snippet.index,
        })
        return { snippet: snippet.id }
      },
      () => this.scrollToSnippet(snippet)
    )
  }

  updateKeywordInput = (e) => {
    clearTimeout(this.keywordTimeout)
    this.setState({ keywordInput: e.target.value }, () => {
      this.keywordTimeout = setTimeout(() => this.updateKeyword(), 400)
    })
  }

  updateKeyword = () => {
    clearTimeout(this.keywordTimeout)
    this.setState((state, props) => ({
      keyword: state.keywordInput,
    }))
  }

  formatMarkedText = (snippet) => {
    let text = []
    let lastIndex = 0
    for (const match of snippet.matches) {
      text.push(
        snippet.text.substring(lastIndex, match.start),
        <mark key={match.start}>
          {snippet.text.substring(match.start, match.end)}
        </mark>
      )
      lastIndex = match.end
    }
    text.push(snippet.text.substring(lastIndex))

    return text
  }

  _renderFullTranscript = (snippets) => {
    if (!snippets || snippets.length === 0) return null

    let chunks = []
    let currentLine = snippets[0].line
    let currentSpeaker = snippets[0].speaker
    let currentChunk = []

    chunks.push(
      <p key={`speaker-${currentLine}`} className="speaker">
        <b>{currentSpeaker}</b>
      </p>
    )
    for (const snippet of snippets) {
      if (currentLine !== snippet.line) {
        chunks.push(<p key={`line-${currentLine}`}>{currentChunk}</p>)

        currentChunk = []
        currentLine = snippet.line
      }

      if (currentSpeaker !== snippet.speaker) {
        chunks.push(
          <p key={`speaker-${currentLine}`} className="speaker">
            <b>{snippet.speaker}</b>
          </p>
        )
        currentSpeaker = snippet.speaker
      }

      // Mark up the parts of the snippet that match the keyword
      let text = this.formatMarkedText(snippet)

      currentChunk.push(
        <span
          key={snippet.id}
          className={
            snippet.id === this.state.snippet ? "selected-snippet" : null
          }
          ref={(ref) => (this[`fulltranscript-${snippet.id}`] = ref)}
        >
          {currentChunk.length > 0 && " "}
          {text}
        </span>
      )
    }

    chunks.push(<p key={`line-${currentLine}`}>{currentChunk}</p>)

    return chunks
  }

  _renderMatchingSnippets = (snippets) => {
    if (!snippets || !this.state.keyword) return null

    if (!snippets || snippets.length === 0) {
      return (
        <p className="no-results">
          No snippets matching "{this.state.keyword}"
        </p>
      )
    }

    return snippets.map((snippet, i) => (
      <div
        key={snippet.id}
        className={`venn-transcript-text snippet ${
          this.state.snippet === snippet.id ? "selected" : ""
        }`}
        onClick={() => this.selectSnippet(snippet, i)}
      >
        <p>{this.formatMarkedText(snippet)}</p>
      </div>
    ))
  }

  render = () => {
    const queryConfig = {
      query: snippetsQuery,
      fetchPolicy: "cache-and-network",
      variables: {
        id: this.props.productId,
        keyword: this.state.keyword,
      },
    }

    return (
      <Query {...queryConfig}>
        {({ data, error, networkStatus, fetchMore, client }) => {
          if (
            !data ||
            !data.page ||
            data.page.proprietaryDataset !== "VENN" ||
            (!data.page.isSpecialAccess && !data.page.free) ||
            !data.page.snippets ||
            data.page.snippets.length === 0
          ) {
            return null
          }

          this.client = client
          this.product = data.page
          return (
            <React.Fragment>
              {this.props.marketplaceView && (
                <h3 className="transcription-search-title">
                  Transcription Search
                </h3>
              )}

              <div
                className={`venn-transcript ${this.props.card ? "card" : ""}`}
              >
                <div className="venn-transcript-section results">
                  <div className="d-flex">
                    <SearchBar
                      classes={["border"]}
                      placeholder="Input a keyword to search"
                      value={this.state.keywordInput}
                      onChange={this.updateKeywordInput}
                      onMagGlassClick={this.updateKeyword}
                      onKeyUp={(e) => {
                        if (e.which === 13 || e.which === 27) {
                          this.updateKeyword()
                        }
                      }}
                    />
                  </div>
                  {this.state.keyword && data.page.matching && (
                    <div
                      className="scroll-content margin"
                      ref={(ref) => (this.matches = ref)}
                    >
                      {this._renderMatchingSnippets(data.page.matching)}
                      {networkStatus === 7 &&
                        data.page.matchingCount > data.page.matching.length && (
                          <Waypoint
                            onEnter={() => {
                              fetchMore({
                                variables: {
                                  offset_matching: data.page.matching.length,
                                },
                                updateQuery: (prev, { fetchMoreResult }) => {
                                  if (!fetchMoreResult) return prev
                                  return Object.assign({}, prev, {
                                    page: {
                                      ...prev.page,
                                      snippetCount:
                                        fetchMoreResult.page.snippetCount,
                                      matchingCount:
                                        fetchMoreResult.page.matchingCount,
                                      matching: [
                                        ...(prev.page.matching || []),
                                        ...(fetchMoreResult.page.matching ||
                                          []),
                                      ],
                                    },
                                  })
                                },
                              })
                            }}
                            fireOnRapidScroll
                            bottomOffset="-70%"
                            scrollableAncestor={this.matches}
                          />
                        )}
                    </div>
                  )}
                </div>
                <div className="venn-transcript-section">
                  <div className="zoom-buttons">
                    <button
                      disabled={this.state.scale <= MIN_SCALE}
                      onClick={this.decScale}
                    >
                      <span className="glyphicon glyphicon-zoom-out" />
                    </button>
                    <button
                      disabled={this.state.scale >= MAX_SCALE}
                      onClick={this.incScale}
                    >
                      <span className="glyphicon glyphicon-zoom-in" />
                    </button>
                  </div>
                  <div
                    className={`venn-transcript-text full-transcript scale-${this.state.scale}`}
                  >
                    <div
                      className="scroll-content"
                      ref={(ref) => (this.fulltranscript = ref)}
                    >
                      {this._renderFullTranscript(data.page.snippets)}
                      {networkStatus === 7 &&
                        data.page.snippetCount > data.page.snippets.length && (
                          <Waypoint
                            onEnter={() => {
                              fetchMore({
                                variables: {
                                  offset_snippets: data.page.snippets.length,
                                },
                                updateQuery: (prev, { fetchMoreResult }) => {
                                  if (!fetchMoreResult) return prev
                                  return Object.assign({}, prev, {
                                    page: {
                                      ...prev.page,
                                      snippetCount:
                                        fetchMoreResult.page.snippetCount,
                                      matchingCount:
                                        fetchMoreResult.page.matchingCount,
                                      snippets: [
                                        ...(prev.page.snippets || []),
                                        ...(fetchMoreResult.page.snippets ||
                                          []),
                                      ],
                                    },
                                  })
                                },
                              })
                            }}
                            fireOnRapidScroll
                            bottomOffset="-70%"
                            scrollableAncestor={this.fulltranscript}
                          />
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          )
        }}
      </Query>
    )
  }
}

const snippetsQuery = gql`
  query vennTranscriptSnippets(
    $id: String!
    $keyword: String!
    $offset_matching: Int
    $offset_snippets: Int
  ) {
    page(id: $id, type: "product") {
      ... on Product {
        id
        name
        proprietaryDataset
        isSpecialAccess
        free
        matchingCount: snippetCount(keyword: $keyword)
        snippetCount
        matching: snippets(keyword: $keyword, offset: $offset_matching) {
          id
          line
          text
          index
          matches(keyword: $keyword) {
            start
            end
          }
        }
        snippets(offset: $offset_snippets) {
          id
          line
          speaker
          text
          matches(keyword: $keyword) {
            start
            end
          }
        }
      }
    }
  }
`

const refetchSnippetsQuery = gql`
  query refetchSnippetsQuery($id: String!, $keyword: String!, $limit: Int) {
    page(id: $id, type: "product") {
      ... on Product {
        id
        snippetCount
        snippets(limit: $limit) {
          id
          line
          speaker
          text
          matches(keyword: $keyword) {
            start
            end
          }
        }
      }
    }
  }
`

VennTranscript.propTypes = {
  initialKeyword: PropTypes.string,
  productId: PropTypes.string.isRequired,
  card: PropTypes.bool,
  trackEvent: PropTypes.func.isRequired,
}

VennTranscript.defaultProps = {
  initialKeyword: "",
}

export default VennTranscript
